
import Vue from 'vue'
import Vuex from 'vuex'
import { format, isPast } from 'date-fns'

Vue.use(Vuex)

export function createStore (config, axios) {
  const APPLICATION_VERSION = config.APPLICATION_VERSION
  const STORAGE_TOKEN_ID = config.STORAGE_TOKEN_ID
  const STORAGE_PERSIST_ID = config.STORAGE_PERSIST_ID
  const CAMPUS_API_HOST = config.CAMPUS_API_HOST
  const CAMPUS_API_KEY = config.CAMPUS_API_KEY
  let CAMPUS_AUTH_KEY = null

  const makeUrl = (route) => {
    return [CAMPUS_API_HOST, route].join('/')
  }

  const makeHeaders = () => {
    let authorization = [`ApiKey ${CAMPUS_API_KEY}`]
    if (CAMPUS_AUTH_KEY && CAMPUS_AUTH_KEY.token) {
      authorization.push(`Bearer ${CAMPUS_AUTH_KEY.token}`)
    }
    return {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': authorization.join(', ')
      }
    }
  }

  const get = (route) => {
    return axios.get(makeUrl(route), makeHeaders())
  }

  const post = (route, payload) => {
    return axios.post(makeUrl(route), payload, makeHeaders())
  }

  const persistState = (state) => {
    const jsonState = JSON.stringify(state)
    localStorage.setItem(STORAGE_PERSIST_ID, jsonState)
  }

  const restoreState = (state) => {
    const jsonState = localStorage.getItem(STORAGE_PERSIST_ID)
    if (jsonState) {
      const persistedState = JSON.parse(jsonState)
      state.user = persistedState.user
      state.contacts = persistedState.contacts
      state.lastUpdated = persistedState.lastUpdated
      state.query = persistedState.query
      state.sortOrder = persistedState.sortOrder
    }
  }

  const destroyState = () => {
    localStorage.removeItem(STORAGE_PERSIST_ID)
  }

  const sortContacts = (contacts, sortByProperty) => {
    let unsortedContacts = []
    for (let key of Object.keys(contacts)) {
      if (contacts[key]) {
        unsortedContacts.push(contacts[key])
      }
    }

    if (!sortByProperty || !sortByProperty.property) {
      return unsortedContacts
    }

    const sortedContacts = unsortedContacts.sort((a, b) => {
      let propA = a[sortByProperty.property]
      let propB = b[sortByProperty.property]

      const NUMBER_TYPE = 'number'
      if (!typeof propA === NUMBER_TYPE) {
        propA = String(propA).toUpperCase()
        propB = String(propB).toUpperCase()
      }

      if (propA < propB) {
        return -1 * sortByProperty.direction
      }
      if (propA > propB) {
        return 1 * sortByProperty.direction
      }
      return 0
    })

    return sortedContacts
  }

  const filterContacts = (contacts, query) => {
    const isPlainObject = (object) => {
      return Object.prototype.toString.call(object) === '[object Object]'
    }
    const objectMatchesQuery = (object, query, depth) => {
      if (!query) {
        return true
      }
      if (isPlainObject(object) && depth > 0) {
        for (let key of Object.keys(object)) {
          if (objectMatchesQuery(object[key], query, depth - 1)) {
            return true
          }
        }
        return false
      } else {
        return `${object}`.indexOf(query) >= 0
      }
    }

    let filteredContacts = []
    for (let contact of contacts) {
      if (objectMatchesQuery(contact, query, 1)) {
        filteredContacts.push(contact)
      }
    }

    return filteredContacts
  }

  const store = new Vuex.Store({
    strict: true,

    state: {
      initialized: false,
      forceLogout: false,
      loading: false,
      user: null,
      query: null,
      sortOrder: {
        property: 'lastname',
        direction: 1
      },
      contacts: {},
      lastUpdated: '1970-01-01'
    },

    getters: {
      user: state => state.user,
      initialized: state => state.initialized,
      forceLogout: state => state.forceLogout,
      getSortOrder: state => state.sortOrder,
      contacts: state => {
        const sortedContacts = sortContacts(state.contacts, state.sortOrder)
        return filterContacts(sortedContacts, state.query)
      },
      getContactById: state => id => {
        return state.contacts[id]
      },
      getLastUpdated: state => state.lastUpdated,
      getApplicationVersion: () => APPLICATION_VERSION,
      getIsLoading: state => state.loading
    },

    actions: {
      async init ({ commit, dispatch }) {
        // Implement this root action in modules

        // Initial loading procedure
        // 1. Get the token from the local storage
        let token = localStorage.getItem(STORAGE_TOKEN_ID)
        token = token ? JSON.parse(token) : null
        commit('setToken', token)

        if (CAMPUS_AUTH_KEY &&
          CAMPUS_AUTH_KEY.valid_until &&
          isPast(new Date(CAMPUS_AUTH_KEY.valid_until))) {
          await dispatch('logout')
        }

        // 2. Restore State from local storage
        commit('initializeState')

        // 3. Unlock the application and allow the router to redirect
        commit('setInitialized')
        try {
          // 4. try to fetch the logged in user
          await dispatch('fetchCurrentUser')
          await dispatch('updateContactList')
        } catch (error) {
          // (might fail) due to several reasons (e.g. token expired)
        }
      },
      async fetchCurrentUser ({ commit, dispatch }) {
        try {
          const { data } = await get('x/api/auth/user')

          if (!data || !data.email) {
            throw new Error()
          }

          commit('setUser', data)
        } catch (error) {
          // only revoke token if a 403 error was returned
          if (error.response && error.response.status === 403) {
            commit('setToken', null)
            commit('setUser', null)
            dispatch('logout')
          }

          throw new Error('Couldn\'t fetch current user!')
        }
      },
      async login ({ commit, dispatch }, payload) {
        try {
          const { data } = await post('x/api/auth', payload)

          if (!data || !data.token) {
            throw new Error()
          }

          commit('setToken', data)
        } catch (error) {
          commit('setToken', null)
          commit('setUser', null)
          throw new Error('Login Failed. Invalid credentials.')
        }
      },
      async logout ({ commit }) {
        try {
          await post('x/api/auth/logout')

          commit('setToken', null)
          commit('setUser', null)
          commit('setForceLogout')
          destroyState()
        } catch (error) {
          commit('setToken', null)
          commit('setUser', null)
          commit('setForceLogout')
          destroyState()
        }
      },
      async updateContactList ({ state, commit }) {
        try {
          commit('setIsLoading', true)
          const lastUpdate = format(new Date(), 'YYYY-MM-DD HH:mm:ss')
          const { data } = await get(`x/api/addressbook?after=${state.lastUpdated}`)

          commit('setContacts', data)
          commit('setLastUpdated', lastUpdate)
          commit('setIsLoading', false)
        } catch (error) {
          commit('setIsLoading', false)
          throw new Error('Couldn\'t update contact list.')
        }
      }
    },

    mutations: {
      setToken (state, token) {
        if (!token) {
          CAMPUS_AUTH_KEY = null
          localStorage.removeItem(STORAGE_TOKEN_ID)
        } else {
          CAMPUS_AUTH_KEY = token
          localStorage.setItem(STORAGE_TOKEN_ID, JSON.stringify(token))
        }
      },
      setUser (state, user) {
        state.user = user
      },
      initializeState (state) {
        restoreState(state)
      },
      setInitialized (state) {
        state.initialized = true
      },
      setForceLogout (state) {
        state.forceLogout = true
      },
      setContacts (state, contacts) {
        for (let contact of contacts) {
          state.contacts[contact.id] = contact
        }
        // force rerender
        Vue.set(state.contacts, state.contacts)
      },
      setLastUpdated (state, lastUpdated) {
        state.lastUpdated = lastUpdated
        persistState(state)
      },
      setSortOrder (state, sortOrder) {
        state.sortOrder = sortOrder
        persistState(state)
      },
      setQuery (state, query) {
        state.query = query
      },
      setIsLoading (state, loading) {
        state.loading = loading
      }
    },

    modules: { }
  })

  // Webpack HRM
  if (module.hot) {
    module.hot.accept([
      // './user'
    ], () => {
      store.hotUpdate({
        modules: {
          // user: require('./user').default
        }
      })
    })
  }

  return store
}



// WEBPACK FOOTER //
// ./src/store/index.js